import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type ProductType = 'private_loan' | 'business_loan' | 'credit_card' | 'savings';

export interface Product {
  id: string;
  provider: string;
  logo?: string;
  rate?: string;
  cashback?: string;
  amount?: string;
  features: string[];
  rating: number;
  link: string;
  product_type: ProductType;
  rates?: {
    period: string;
    rate: string;
  }[];
  minDeposit?: string;
  withdrawalTerms?: string;
  comparison_details?: {
    pros?: string[];
    cons?: string[];
    best_for?: string[];
    requirements?: Record<string, any>;
    terms_summary?: string;
  };
}

interface ComparisonContextType {
  products: Product[];
  productType: ProductType | null;
  addProduct: (product: Product) => void;
  removeProduct: (id: string) => void;
  clearComparison: () => void;
  canAddProduct: (product: Product) => boolean;
  isInComparison: (id: string) => boolean;
}

const ComparisonContext = createContext<ComparisonContextType | undefined>(undefined);

export const ComparisonProvider = ({ children }: { children: ReactNode }) => {
  const [products, setProducts] = useState<Product[]>(() => {
    const saved = localStorage.getItem('comparison-products');
    return saved ? JSON.parse(saved) : [];
  });
  
  const [productType, setProductType] = useState<ProductType | null>(() => {
    const saved = localStorage.getItem('comparison-type');
    return saved as ProductType | null;
  });

  useEffect(() => {
    localStorage.setItem('comparison-products', JSON.stringify(products));
    localStorage.setItem('comparison-type', productType || '');
  }, [products, productType]);

  const canAddProduct = (product: Product): boolean => {
    if (products.length >= 3) return false;
    if (products.length === 0) return true;
    return productType === product.product_type;
  };

  const addProduct = (product: Product) => {
    if (products.find(p => p.id === product.id)) {
      removeProduct(product.id);
      return;
    }

    if (products.length >= 3) {
      return;
    }

    if (productType && productType !== product.product_type) {
      return;
    }

    setProducts([...products, product]);
    setProductType(product.product_type);
  };

  const removeProduct = (id: string) => {
    const newProducts = products.filter(p => p.id !== id);
    setProducts(newProducts);
    if (newProducts.length === 0) {
      setProductType(null);
    }
  };

  const clearComparison = () => {
    setProducts([]);
    setProductType(null);
  };

  const isInComparison = (id: string): boolean => {
    return products.some(p => p.id === id);
  };

  return (
    <ComparisonContext.Provider
      value={{
        products,
        productType,
        addProduct,
        removeProduct,
        clearComparison,
        canAddProduct,
        isInComparison,
      }}
    >
      {children}
    </ComparisonContext.Provider>
  );
};

export const useComparison = () => {
  const context = useContext(ComparisonContext);
  if (!context) {
    throw new Error('useComparison must be used within ComparisonProvider');
  }
  return context;
};
